<template>
  <div class="entries-container">
    <div class="page-header">
      <div class="page-header-main">
        <button type="button" class="btn-back" @click="goBack" title="Voltar">
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">
            <i class="fa-solid fa-receipt"></i>
            Detalhes da Aposta
          </h1>
          <p class="page-subtitle">
            <span v-if="bet.nsu">Poule {{ bet.nsu }}</span>
            <span v-if="bet.sellerName"> · {{ bet.sellerName }}</span>
          </p>
        </div>
      </div>
    </div>

    <div v-if="!accessOk && !loading" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-ban"></i>
        <h3>Acesso Negado</h3>
        <p>Esta aposta pertence a uma região que não está sob sua responsabilidade.</p>
        <button type="button" class="btn-primary" @click="goBack">Voltar</button>
      </div>
    </div>

    <div v-else-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados da aposta...</span>
      </div>
    </div>

    <div v-else-if="errorLoading" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <h3>Erro ao carregar</h3>
        <p>{{ errorMessage }}</p>
        <button type="button" class="btn-primary" @click="goBack">Voltar</button>
      </div>
    </div>

    <div v-else class="results-card">
      <div class="summary-grid">
        <div class="summary-item">
          <span class="summary-label">Poule</span>
          <strong>{{ bet.nsu || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span class="summary-label">Vendedor</span>
          <strong>{{ bet.sellerName || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span class="summary-label">Região</span>
          <strong>{{ bet.regionName || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span class="summary-label">Status</span>
          <strong>{{ bet.status || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span class="summary-label">Impressão</span>
          <strong>{{ dateCurrent }}</strong>
        </div>
      </div>

      <div
        v-for="lottery in bet.lotteries"
        :key="lottery.lotteryDetailId || lottery.lotteryDetailName"
        class="lottery-block"
      >
        <div class="lottery-header">
          {{ lottery.lotteryDetailName }}
        </div>
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Modalidade</th>
                <th>Colocação</th>
                <th>Palpite</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(numberItem, index) in lottery.numbers" :key="`${lottery.lotteryDetailId}-${index}`">
                <tr>
                  <td>{{ numberItem.modalityDescription }}</td>
                  <td>{{ numberItem.placing }}</td>
                  <td>{{ numberItem.number }}</td>
                  <td>
                    <span class="currency-value">{{ setCurrencyString(numberItem.value) }}</span>
                  </td>
                </tr>
                <tr
                  v-for="(award, awardIndex) in (numberItem.numberAwards || [])"
                  :key="`award-${index}-${awardIndex}`"
                  class="award-row"
                >
                  <td colspan="4">
                    🎉 Prêmio: {{ setCurrencyString(award.value) }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="totals-card">
        <div class="total-row">
          <span>Total</span>
          <strong>{{ setCurrencyString(total) }}</strong>
        </div>
        <div class="total-row">
          <span>Comissão</span>
          <strong>{{ setCurrencyString(bet.commission) }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import jsFunctions from "@/services/jsFunctions";
import mainService from "@/services/mainService";
import { handleErrorMessage } from "@/services/config";
import { toast } from "@/composables/useToast";
import { ROLES } from "@/services/permissions";

export default {
  name: "ViewEntriesNumbersDetail",
  data: () => ({
    bet: {
      amount: 0,
      lotteries: [],
      nsu: "",
      sellerName: "",
      regionName: "",
      status: "",
      commission: 0,
      regionId: null,
    },
    dateCurrent: new Date().toLocaleString(),
    total: 0,
    betId: null,
    loading: true,
    errorLoading: false,
    errorMessage: "",
    accessOk: false,
    currentUser: null,
    userResponsibleRegions: [],
  }),
  computed: {
    isAdmin() {
      return this.currentUser && parseInt(this.currentUser.role) === ROLES.ADMIN;
    },
  },
  async mounted() {
    await this.loadCurrentUser();
    this.parseQueryParams();
    await this.searchBet();
  },
  methods: {
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem("user");
        if (!userData) {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push("/login");
          return;
        }

        this.currentUser = JSON.parse(userData);
        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = (response.data || []).map((region) => region.id);

        if (
          this.currentUser.regionId &&
          !this.userResponsibleRegions.some((id) => String(id) === String(this.currentUser.regionId))
        ) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
      }
    },

    parseQueryParams() {
      const query = this.$route.query || {};
      this.betId = query.betId || null;

      if (!this.betId) {
        this.errorLoading = true;
        this.errorMessage = "ID da aposta não informado na URL";
      }
    },

    validateAccess() {
      if (!this.currentUser) {
        this.accessOk = false;
        return;
      }

      if (this.isAdmin) {
        this.accessOk = true;
        return;
      }

      if (!this.bet.regionId) {
        this.accessOk = true;
        return;
      }

      this.accessOk = this.userResponsibleRegions.some(
        (id) => String(id) === String(this.bet.regionId)
      );

      if (!this.accessOk) {
        toast.error("Você não tem permissão para acessar esta aposta!");
      }
    },

    async searchBet() {
      if (this.errorLoading || !this.betId) {
        this.loading = false;
        return;
      }

      try {
        this.loading = true;
        this.total = 0;

        const response = await mainService.searchBetById(this.betId);
        const payload = response.data || {};
        // ActionResultConverter devolve o Result direto
        this.bet = payload.result || payload.Result || payload;

        if (!this.bet || typeof this.bet !== "object") {
          throw new Error("Resposta inválida da API");
        }

        if (!Array.isArray(this.bet.lotteries)) {
          this.bet.lotteries = this.bet.Lotteries || [];
        }

        this.bet.lotteries.forEach((lottery) => {
          const numbers = lottery.numbers || lottery.Numbers || [];
          lottery.numbers = numbers;
          numbers.forEach((numberItem) => {
            this.total += Number(numberItem.value || numberItem.Value || 0);
          });
        });

        this.validateAccess();
      } catch (error) {
        this.errorLoading = true;
        this.errorMessage = handleErrorMessage(error);
        toast.error("Erro ao carregar dados da aposta");
      } finally {
        this.loading = false;
      }
    },

    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push("/entries");
    },
  },
};
</script>

<style scoped>
.page-header-main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.page-title {
  display: flex;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-top: 0.25rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
  color: var(--text-primary);
  cursor: pointer;
  flex-shrink: 0;
}

.btn-back:hover {
  background: var(--hover-color, rgba(0, 0, 0, 0.04));
}

.loading-container,
.empty-state {
  display: flex;
  justify-content: center;
  padding: 3rem 1rem;
}

.loading-spinner,
.empty-state-content {
  text-align: center;
  color: var(--text-primary);
}

.empty-state-content i {
  font-size: 2.5rem;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
}

.results-card {
  padding: 1rem;
  overflow: visible !important;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--bg-secondary, transparent);
  text-align: left;
}

.summary-label {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.lottery-block {
  margin-bottom: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}

.lottery-header {
  padding: 0.75rem 1rem;
  background: var(--accent-color, #41b883);
  color: #fff;
  font-weight: 600;
}

.table-container {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th,
.modern-table td {
  padding: 0.75rem 1rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.modern-table th {
  background: #e5e5e5;
  font-weight: 600;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
}

.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
}

.award-row td {
  font-style: italic;
  background: rgba(59, 130, 246, 0.08);
}

.totals-card {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  color: var(--text-primary);
}

[data-theme="dark"] .totals-card {
  background: rgba(255, 255, 255, 0.06);
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
}

.btn-primary {
  margin-top: 1rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 8px;
  background: var(--accent-color, #3b82f6);
  color: #fff;
  cursor: pointer;
}
</style>
